<template>
  <div
    class="checkbox-list"
    role="group"
    :aria-label="label"
  >
    <button
      v-for="option in options"
      :key="option.id"
      type="button"
      class="checkbox-list__item"
      :class="{ 'checkbox-list__item--checked': isChecked(option) }"
      :aria-pressed="isChecked(option)"
      :disabled="disabled"
      @click="toggle(option)"
    >
      <span class="checkbox-list__box">
        <i
          v-if="isChecked(option)"
          class="exu-check checkbox-list__mark"
        />
      </span>
      <span class="checkbox-list__label">{{ option.label }}</span>
    </button>
  </div>
</template>

<script>
/**
 * CheckboxList — список вариантов с галочками, выбор нескольких.
 *
 * Значение — массив идентификаторов выбранных вариантов. Тип идентификатора
 * сохраняется тем же, что пришёл в `options`: сервер присылает числа и ждёт
 * числа обратно, поэтому сравнение идёт по строковому виду, а в модель
 * кладётся исходный `option.id`.
 *
 * @example
 * <checkbox-list
 *   v-model="selected"
 *   :options="[{ id: 1, label: 'Первый' }, { id: 2, label: 'Второй' }]"
 *   label="Выберите варианты"
 * />
 */
export default {
  name: 'CheckboxList',
  props: {
    /** Идентификаторы выбранных вариантов */
    modelValue: {
      type: Array,
      default: () => [],
    },
    /** Варианты: [{ id, label }] */
    options: {
      type: Array,
      default: () => [],
    },
    /** Подпись группы для скринридера */
    label: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  methods: {
    isChecked(option) {
      return this.modelValue.some((id) => String(id) === String(option.id));
    },
    toggle(option) {
      if (this.disabled) return;
      const next = this.isChecked(option)
        ? this.modelValue.filter((id) => String(id) !== String(option.id))
        : [...this.modelValue, option.id];
      this.$emit('update:modelValue', next);
    },
  },
};
</script>

<style lang="scss" scoped>
.checkbox-list {
  display: flex;
  flex-direction: column;
  gap: 4px;

  &__item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    width: 100%;
    padding: 8px 0;
    border: none;
    background: none;
    text-align: left;
    cursor: pointer;

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  }

  &__box {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    margin-top: 1px;
    border: 1.5px solid #b6c2d1;
    border-radius: 6px;
    background: #ffffff;
    color: #ffffff;
    font-size: 13px;
  }

  // акцентный оранжевый — тот же, что у кнопки подтверждения в визитке
  &__item--checked &__box {
    border-color: #ff957d;
    background: #ff957d;
  }

  &__label {
    font-size: 14px;
    line-height: 1.35;
    color: #1f1f1f;
  }
}
</style>
